import { autoinject, PLATFORM } from 'aurelia-framework';

import { ClassicPanel } from '~/components/classic-panel/classic-panel';
import {
	ClassicPanelDialog,
	ClassicPanelDialogModel,
	ClassicPanelDialogModelDefaults,
} from '~/components/classic-panel/classic-panel-dialog';
import { Notification } from '~/components/notification/notification';
import { ShortcutKey } from '~/constants';
import {
	ProcessBatchDialog,
	ProcessBatchDialogModel,
} from '~/forms/batch-processing-centre/process-batch-dialog';
import { BatchProcessFieldName } from '~/models/entities/batch-process';
import { FormModel } from '~/models/form-model';
import { InsertResult, isInsertResult } from '~/models/insert-result';
import { FetchUtils } from '~/utils/fetch-utils';
import { HelpFileUtils } from '~/utils/help-file-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';

export interface NewBatchDialogModel extends ClassicPanelDialogModel {
	cancelBatch?: boolean;
	cancelTransactions?: boolean;
	formModel?: FormModel;
	functionName?: string;
	recordId?: number;
	reverseTransactions?: boolean;
}

const NewBatchDialogModelDefaults: NewBatchDialogModel = {
	...ClassicPanelDialogModelDefaults,
	cancelButtonText: 'clickable-elements.close',
	dialogSize: ClassicPanelDialog.DialogSize.MEDIUM,
};

@autoinject
export class NewBatchDialog extends ClassicPanelDialog<NewBatchDialogModel> {
	private static readonly BATCH_PROCESSING_CENTRE_FUNCTION_NAME =
		'batch-processing-centre';

	constructor(...rest) {
		super(...rest);
		PLATFORM.moduleName('./new-batch-dialog');
	}

	public activate(model: NewBatchDialogModel) {
		model.areaName = 'newBatch';
		return super.activate(model, NewBatchDialogModelDefaults);
	}

	/**
	 * 'Save and Process' action
	 * Used via `click.trigger` in template
	 */
	private async saveAndMaybeProcess(): Promise<void> {
		Logger.log('Saving and processing new batch...');
		this.setIsSaving(true);
		this.classicPanel.setIsEditing(false);
		try {
			super
				.doSaveApi()
				.then(async (saveResult: FormModel | InsertResult) => {
					if (isInsertResult(saveResult)) {
						Notification.success(
							this.i18n.tr(
								'successes.this-record-was-successfully-added',
							),
						);
						const formModel = await ClassicPanel.getFormModel(
							this.classicFormsApi,
							this.model.functionName,
							saveResult.id,
						);
						const model: ProcessBatchDialogModel = { formModel };
						try {
							const result = await this.dialogUtils
								.showCustomDialog(ProcessBatchDialog, model)
								.whenClosed((_) => _);
							if (!result.wasCancelled) {
								Notification.success(
									this.i18n.tr(
										'batch-processing-centre:processing-batch-with-ref',
										{
											ref: formModel.fields[
												BatchProcessFieldName
													.BSl_Batch_Reference
											].value,
										},
									),
								);
							}
							this.controller.ok();
						} catch (error) {
							Notification.error(
								this.i18n.tr(
									'batch-processing-centre:unable-to-save-and-process-this-batch',
									{
										message:
											FetchUtils.normalizeError(error),
									},
								),
							);
						}
					} else {
						Logger.error(
							'Expected to receive the id of the new batch',
						);
						Notification.error(
							this.i18n.tr(
								'errors.system-error-occurred-inserting-a-batch-record',
							),
						);
					}
				});
		} catch (error) {
			let errorMessage: string;
			if (error) {
				const rejectionMessage = FetchUtils.normalizeError(error);
				errorMessage = this.i18n.tr(
					'errors.unable-to-add-this-record',
					{ message: rejectionMessage },
				);
			} else {
				errorMessage = this.i18n.tr('errors.unable-to-add-this-record');
			}
			Notification.error(errorMessage);
		}
		this.setIsSaving(false);
		this.classicPanel.setIsEditing(true);
	}

	/**
	 * @override
	 */
	protected attachKeyboardShortcuts() {
		super.attachKeyboardShortcuts();
		KeyboardShortcut.subscribe(this, ShortcutKey.HelpFile, () =>
			this.openHelpFile(),
		);
	}

	/**
	 * Used by view
	 */
	private openHelpFile(): void {
		HelpFileUtils.openHelpFileIfPossible(
			NewBatchDialog.BATCH_PROCESSING_CENTRE_FUNCTION_NAME,
			undefined,
		);
	}
}
